import { useSchoolYear } from '@pelckmans/business-components/context/SchoolYearContext';
import Checkbox from '@pelckmans/elementis/components/checkbox';
import { endOfDay, isBefore } from 'date-fns';
import { useEffect, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import LocalizedDateField, { dateFormatter } from '../elementis/components/localized-date-field';
import type { GroupAudience, MemberAudience } from './sharedMaterialApi';
import { useShareModalContext } from './useShareModalContext';

const BASE = 'PAGES.TOC_LEVEL.MODALS.SHARE.ASSIGNMENT';

interface AssignmentActionAreaProps {
  disabled: boolean;
  containsNonScorable: boolean;
  audience?: GroupAudience | MemberAudience;
  parentAudience?: GroupAudience;
  indeterminate?: boolean;
  indeterminateRepeatable?: boolean;
}

const renderDateRangeValue = (range: DateRange) =>
  Object.values(range)
    .map(d => (d ? dateFormatter.format(d) : '...'))
    .join(' - ');

export const AssignmentActionArea = ({
  disabled,
  containsNonScorable,
  audience,
  parentAudience,
  indeterminate,
  indeterminateRepeatable,
}: AssignmentActionAreaProps) => {
  const { t } = useTranslation();
  const { handleAssignmentToggle, handleUpdateAudience } = useShareModalContext();
  const {
    currentSchoolYear: { to: maxDate },
  } = useSchoolYear();

  const TODAY = new Date();

  const hasStartDateInThePast =
    !!audience?.assignment?.from &&
    !!audience?.assignment?.id && // Only consider existing assignments
    isBefore(new Date(audience?.assignment?.from), TODAY);
  const minDate = hasStartDateInThePast ? new Date(audience!.assignment!.from!) : TODAY;

  const [dateRange, setDateRange] = useState<DateRange | undefined>();

  const userId = audience && 'userId' in audience ? audience.userId : undefined;

  // Sync local dateRange state when audience or parentAudience changes
  useEffect(() => {
    const audienceFrom = audience?.assignment?.from;
    const audienceTo = audience?.assignment?.to;

    const parentFrom = parentAudience?.assignment?.from;
    const parentTo = parentAudience?.assignment?.to;

    if (audienceFrom && audienceTo) {
      setDateRange({ from: new Date(audienceFrom), to: new Date(audienceTo) });
    } else if (parentFrom && parentTo) {
      setDateRange({ from: new Date(parentFrom), to: new Date(parentTo) });
    } else if (dateRange && !audienceFrom && !audienceTo) {
      setDateRange(undefined);
    } else if (dateRange && !parentFrom && !parentTo && dateRange) {
      setDateRange(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience?.assignment, parentAudience?.assignment]);

  // Sync audience assignment when dateRange changes
  useEffect(() => {
    const dateRangeFrom = dateRange?.from?.toISOString();
    const dateRangeTo = dateRange?.to ? endOfDay(dateRange.to).toISOString() : undefined;

    if (
      dateRangeFrom &&
      dateRangeTo &&
      (audience?.assignment?.from !== dateRangeFrom || audience?.assignment?.to !== dateRangeTo)
    ) {
      const from = hasStartDateInThePast ? audience?.assignment?.from : dateRangeFrom;

      handleUpdateAudience({
        groupId: audience?.groupId,
        memberId: userId,
        newAssignment: {
          from,
          to: dateRangeTo,
        },
      });
    }
  }, [audience, dateRange, handleUpdateAudience, userId, hasStartDateInThePast]);

  return (
    <div className="share-modal-action-area">
      <Checkbox
        key="assignment"
        checked={!!audience?.assignment || !!parentAudience?.assignment}
        disabled={disabled}
        indeterminate={indeterminate}
        onChange={() => handleAssignmentToggle(audience?.groupId, userId)}
      >
        {t(`${BASE}.CHECKBOX_LABEL`)}
      </Checkbox>
      <LocalizedDateField
        className="share-modal__date-field"
        size="sm"
        mode="range"
        selected={dateRange}
        onSelect={setDateRange}
        renderValue={renderDateRangeValue}
        aria-label={t(`${BASE}.DATE_FIELD.A11Y_LABEL`)}
        placeholder={indeterminate ? t(`${BASE}.DATE_FIELD.MULTIPLE`) : t(`${BASE}.DATE_FIELD.PLACEHOLDER`)}
        dateMenuAriaLabel={t(`${BASE}.DATE_FIELD.DATE_MENU_A11Y_LABEL`)}
        minDate={minDate}
        maxDate={maxDate}
        disabled={!audience?.assignment}
        error={Boolean(audience?.dateError)}
        required
      />
      {!containsNonScorable && (
        <Checkbox
          key="repeatable"
          checked={!!audience?.assignment?.repeatable || !!parentAudience?.assignment?.repeatable}
          disabled={!audience?.assignment}
          indeterminate={indeterminateRepeatable}
          onChange={() =>
            handleUpdateAudience({
              groupId: audience?.groupId,
              memberId: userId,
              newAssignment: {
                repeatable: !audience?.assignment?.repeatable,
              },
            })
          }
        >
          {t(`${BASE}.REPEATABLE_CHECKBOX_LABEL`)}
        </Checkbox>
      )}
    </div>
  );
};
